import { useEffect, useId, useMemo, useRef, useState } from "react";
import { suggestImportMapping, temperature, type CurveImportMapping, type ImportTable } from "@anthemreactv2/roast-domain";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Notice, SelectField } from "./components/workbench";
import { readRoastImportFile, workspaceFromImport, type RoastImportFile } from "./import-file";
import { CurveChart } from "./CurveChart";
import { formatTime, type Workspace } from "./workspace";

type TabularFile = { name: string; tables: ImportTable[] };

/** Both entry points share parsing, preview, diagnostics, and the same unit choices. */
export function RoastImport({ onReady, onReadStart, onBusyChange }: {
  onReady: (workspace: Workspace, trigger: HTMLElement) => void;
  onReadStart?: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const pending = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<TabularFile | null>(null);
  const [pdf, setPdf] = useState<Extract<RoastImportFile, { kind: "pdf" }> | null>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; pending.current?.abort(); }; }, []);

  async function select(selected: File) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); onBusyChange?.(true); onReadStart?.();
    setFilename(selected.name); setError(null); setFile(null); setPdf(null);
    const controller = new AbortController();
    pending.current = controller;
    try {
      const result = await readRoastImportFile(selected, controller.signal);
      if (!mounted.current) return;
      if (controller.signal.aborted) throw new Error("Import cancelled.");
      if (result.kind === "workspace") {
        if (input.current) onReady(result.workspace, input.current);
      } else if (result.kind === "pdf") setPdf(result);
      else setFile(result);
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "Unable to read this file. Choose a CSV, Excel, Cropster PDF, or exported workspace JSON.");
    } finally {
      inFlight.current = false;
      pending.current = null;
      if (mounted.current) { setBusy(false); onBusyChange?.(false); }
    }
  }

  return <div className="min-w-0 space-y-3">
    <div className="space-y-2">
      <Label htmlFor={id}>CSV, Excel, Cropster PDF, or workspace JSON</Label>
      <Input ref={input} id={id} type="file" accept=".csv,.xls,.xlsx,.pdf,.json,.crc" className="h-auto min-h-11 py-2" disabled={busy}
        aria-describedby={`${id}-help`} onChange={event => {
          const selected = event.target.files?.[0];
          event.target.value = "";
          if (selected) void select(selected);
        }} />
      <p id={`${id}-help`} className="text-xs leading-relaxed text-muted-foreground">Import a curve in °C or °F. Cropster PDFs need the printed readings table. Preview the data before opening. Files stay in this browser.</p>
    </div>
    {filename && <p className="break-all text-sm">Selected file: <span className="font-medium">{filename}</span></p>}
    {busy && <div className="flex flex-wrap items-center gap-2"><p role="status" className="text-sm text-muted-foreground">Reading {filename}…</p><Button variant="outline" className="min-h-11" onClick={() => pending.current?.abort()}>Cancel reading</Button></div>}
    {error && <Notice error title="Import needs attention">{error}</Notice>}
    {file && <ImportPreview file={file} onCancel={() => setFile(null)} restoreFocus={() => input.current?.focus()} onConfirm={workspace => {
      setFile(null);
      if (input.current) onReady(workspace, input.current);
    }} />}
    {pdf && <PdfPreview file={pdf} onCancel={() => setPdf(null)} restoreFocus={() => input.current?.focus()} onConfirm={() => {
      setPdf(null);
      if (input.current) onReady(pdf.workspace, input.current);
    }} />}
  </div>;
}

function PdfPreview({ file, onCancel, onConfirm, restoreFocus }: {
  file: Extract<RoastImportFile, { kind: "pdf" }>; onCancel: () => void; onConfirm: () => void; restoreFocus: () => void;
}) {
  const accepted = useRef(false);
  const { workspace, report } = file;
  const spacing = report.intervalMs.min === report.intervalMs.max ? `${report.intervalMs.max / 1000}` : `${report.intervalMs.min / 1000}–${report.intervalMs.max / 1000}`;
  return <Dialog open onOpenChange={open => { if (!open) onCancel(); }}>
    <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-3xl" showCloseButton={false} onCloseAutoFocus={event => {
      event.preventDefault(); if (!accepted.current) restoreFocus();
    }}>
      <DialogHeader className="shrink-0 text-left">
        <DialogTitle>Preview Cropster PDF</DialogTitle>
        <DialogDescription className="[overflow-wrap:anywhere]">{file.name} · Readings and events from the printed tables.</DialogDescription>
      </DialogHeader>
      <div className="min-h-0 space-y-4 overflow-y-auto px-1 py-1">
        <div role="status" className="space-y-1 rounded-lg border bg-muted/30 p-3 text-sm tabular-nums">
          <p className="font-medium">{report.samples.length} readings · {formatTime(workspace.playheadMs)} elapsed · {report.events.length} events</p>
          <p>Source: °{report.unit} · Readings {spacing} seconds apart · {report.hasEnvironment ? "Bean and environment temperatures" : "Bean temperature only"}</p>
        </div>
        <div className="roasting-workbench roast-curve-panel min-w-0"><CurveChart workspace={workspace} unit={report.unit} interval={30} windowSeconds={30} showRor={false} /></div>
        <Notice title="Printed report resolution">Only the printed readings are available. Sparse readings appear as points; gaps stay visible. The PDF’s RoR column is not a temperature and is not imported. RoR calculations need continuous readings.</Notice>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <caption className="p-3 text-left font-medium">Imported events</caption>
            <thead className="bg-muted/50"><tr><th className="p-2">Time</th><th className="p-2">Event / note</th></tr></thead>
            <tbody>{workspace.events.map(event => <tr key={event.id} className="border-t"><td className="p-2 tabular-nums">{formatTime(event.elapsedMs)}</td><td className="p-2 [overflow-wrap:anywhere]">{event.note}</td></tr>)}</tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">{workspace.events.some(event => event.name === "Drop") ? "Drop time comes from the report’s event table." : "No Drop event was printed. Mark it separately if known; the final reading does not establish drop time."} Weights, inventory, and machine settings are not changed by this import.</p>
      </div>
      <DialogFooter className="shrink-0 border-t pt-4">
        <Button className="min-h-11" variant="outline" onClick={onCancel}>Cancel import</Button>
        <Button className="min-h-11" onClick={() => { accepted.current = true; onConfirm(); }}>Use this curve</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

function ImportPreview({ file, onCancel, onConfirm, restoreFocus }: {
  file: TabularFile; onCancel: () => void; onConfirm: (workspace: Workspace) => void; restoreFocus: () => void;
}) {
  const accepted = useRef(false);
  const [sheetIndex, setSheetIndex] = useState(() => {
    const index = file.tables.findIndex(table => {
      const guess = suggestImportMapping(table.rows);
      return guess.timeColumn !== null && guess.beanColumn !== null;
    });
    return Math.max(0, index);
  });
  const table = file.tables[sheetIndex]!;
  const [mapping, setMapping] = useState<CurveImportMapping>(() => suggestImportMapping(table.rows));
  const preview = useMemo(() => {
    try { return { workspace: workspaceFromImport(file.name, table.rows, mapping), error: null }; }
    catch (cause) { return { workspace: null, error: cause instanceof Error ? cause.message : "Check the columns and units before importing." }; }
  }, [file.name, table, mapping]);
  const header = table.rows[mapping.headerRow] ?? [];
  const columnCount = table.rows.reduce((max, row) => Math.max(max, row.length), 0);
  const columns = Array.from({ length: columnCount }, (_, column) => ({
    value: column,
    label: `${column + 1} · ${header[column] === null || header[column] === undefined || header[column] === "" ? "No heading" : String(header[column])}`,
  }));
  const change = <K extends keyof CurveImportMapping>(key: K, value: CurveImportMapping[K]) => setMapping(current => ({ ...current, [key]: value }));
  function changeColumn(key: "timeColumn" | "beanColumn" | "environmentColumn" | "eventColumn", value: string) {
    setMapping(current => ({
      ...current,
      [key]: value === "" ? null : Number(value),
      // A new column cannot inherit units inferred or confirmed for the old one.
      ...(key === "timeColumn" ? { timeFormat: null } : {}),
      ...(key === "beanColumn" || key === "environmentColumn" ? { temperatureUnit: null } : {}),
    }));
  }
  const columnSelect = (label: string, key: "timeColumn" | "beanColumn" | "environmentColumn" | "eventColumn", optional = false) =>
    <SelectField label={label} value={mapping[key] ?? ""} onChange={event => changeColumn(key, event.target.value)}>
      <option value="">{optional ? "Not included" : "Choose a column"}</option>
      {columns.map(column => <option key={column.value} value={column.value}>{column.label}</option>)}
    </SelectField>;
  const workspace = preview.workspace;

  return <Dialog open onOpenChange={open => { if (!open) onCancel(); }}>
    <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-3xl [&_select]:min-h-10" showCloseButton={false} onCloseAutoFocus={event => {
      event.preventDefault();
      if (!accepted.current) restoreFocus();
    }}>
      <DialogHeader className="shrink-0 text-left">
        <DialogTitle>Preview roast import</DialogTitle>
        <DialogDescription><span className="[overflow-wrap:anywhere]">{file.name}</span> · Check that each row is a reading from one roast.</DialogDescription>
      </DialogHeader>
      <div className="min-h-0 space-y-4 overflow-y-auto px-1 py-1">
      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <SelectField label="Worksheet" value={sheetIndex} onChange={event => {
          const nextIndex = Number(event.target.value);
          setSheetIndex(nextIndex); setMapping(suggestImportMapping(file.tables[nextIndex]!.rows));
        }}>
          {file.tables.map((sheet, index) => <option key={index} value={index}>{sheet.name}</option>)}
        </SelectField>
        <SelectField label="Header row" value={mapping.headerRow} onChange={event => {
          const nextHeader = Number(event.target.value);
          setMapping(suggestImportMapping(table.rows, nextHeader));
        }}>
          {table.rows.slice(0, 100).map((row, index) => <option key={index} value={index}>Row {index + 1} · {row.slice(0, 3).map(cell => cell ?? "").join(" / ").slice(0, 100)}</option>)}
        </SelectField>
        {columnSelect("Elapsed time column", "timeColumn")}
        <SelectField label="Time format" value={mapping.timeFormat ?? ""} onChange={event => change("timeFormat", event.target.value === "" ? null : event.target.value as CurveImportMapping["timeFormat"])}>
          <option value="">Choose the file’s time format</option>
          <option value="seconds">Seconds from charge</option>
          <option value="milliseconds">Milliseconds from charge</option>
          <option value="minutes">Minutes from charge</option>
          <option value="clock">mm:ss or hh:mm:ss</option>
          <option value="excel">Excel duration (fraction of a day)</option>
        </SelectField>
        {columnSelect("Bean temperature column", "beanColumn")}
        {columnSelect("Environment temperature column", "environmentColumn", true)}
        <SelectField label="File temperature unit" value={mapping.temperatureUnit ?? ""} onChange={event => change("temperatureUnit", event.target.value === "" ? null : event.target.value as "C" | "F")}>
          <option value="">Choose Celsius or Fahrenheit</option>
          <option value="C">Celsius (°C)</option><option value="F">Fahrenheit (°F)</option>
        </SelectField>
        {columnSelect("Event / note column", "eventColumn", true)}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">Times must be elapsed since charge. Blank temperatures remain gaps. Choose units from the file or your export settings; the chart’s display setting is separate.</p>
      <div className="min-w-0 overflow-x-auto rounded-lg border">
        <table className="w-full text-left text-xs" aria-label="Source file preview">
          <caption className="p-3 text-left font-medium">First rows from the selected worksheet</caption>
          <thead className="bg-muted/50"><tr><th className="p-2">Row</th>{columns.map(column => <th key={column.value} className="max-w-48 p-2 [overflow-wrap:anywhere]">{column.label}</th>)}</tr></thead>
          <tbody>{table.rows.slice(mapping.headerRow + 1, mapping.headerRow + 6).map((row, index) => <tr key={index} className="border-t">
            <th className="p-2 font-normal">{mapping.headerRow + index + 2}</th>
            {columns.map(column => <td key={column.value} className="max-w-48 p-2 tabular-nums [overflow-wrap:anywhere]">{row[column.value] ?? "—"}</td>)}
          </tr>)}</tbody>
        </table>
      </div>
      {preview.error && <Notice error title="Check the import settings">{preview.error}</Notice>}
      {!workspace && <p className="text-sm leading-relaxed text-muted-foreground">A roast summary with one row per batch cannot recreate a curve. Choose a sheet with elapsed time and bean temperature readings. Cropster’s Excel reports may contain only summary data.</p>}
      {workspace && <div role="status" className="space-y-2 rounded-lg border bg-muted/30 p-3 text-sm">
        <p className="font-medium">Ready to import {workspace.source.samples.length.toLocaleString()} readings · {formatTime(workspace.source.samples.at(-1)!.elapsedMs)} elapsed · {workspace.events.length} events</p>
        <p>Temperatures interpreted as °{mapping.temperatureUnit}. Missing readings stay blank.</p>
        <p>First bean reading: {temperature(workspace.source.samples.find(sample => sample.beanC !== null)!.beanC!, mapping.temperatureUnit!).toFixed(1)} °{mapping.temperatureUnit}.</p>
        {mapping.eventColumn === null && <p className="text-muted-foreground">No event column selected. Mark roast milestones after import if they are not in this file.</p>}
      </div>}
      </div>
      <DialogFooter className="shrink-0 border-t pt-4">
        <Button className="min-h-10" variant="outline" onClick={onCancel}>Cancel import</Button>
        <Button className="min-h-10" disabled={!workspace} onClick={() => { if (workspace) { accepted.current = true; onConfirm(workspace); } }}>Use this curve</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
